import { useQuery, type UseQueryOptions } from '@tanstack/react-query';
import { getUserService } from '@src/container';
import type { PaginatedUsersDTO } from '@src/application/users/dto/user.dto';
import type { PaginationParams } from '@src/domain/users/repository/user.repository.interface';

/**
 * Hook: useListUsers
 * Fetches all users with pagination (Admin only)
 * 
 * @param params - Pagination parameters
 * @param options - React Query options
 * @returns Query result with paginated users
 * 
 * @example
 * ```typescript
 * const { data, isLoading, error } = useListUsers({ page: 1, page_size: 20 });
 * ```
 */
export function useListUsers(
    params?: PaginationParams,
    options?: Omit<UseQueryOptions<PaginatedUsersDTO, Error>, 'queryKey' | 'queryFn'>
) {
    const userService = getUserService();

    return useQuery({
        queryKey: ['users', params],
        queryFn: () => userService.listAll(params),
        staleTime: 5 * 1000, // 5 seconds
        ...options,
    });
}
